import { api } from './api';
import type {
  SecretSanta,
  SecretSantaListResponse,
  SecretSantaAssignment,
  CreateSecretSantaDto,
  UpdateSecretSantaDto,
  JoinSecretSantaDto,
  SecretSantaFilters,
  SecretSantaParticipant,
} from '@/types/gift.types';

const BASE_URL = '/secretsanta';

export const secretSantaService = {
  /**
   * Get all Secret Santa groups
   */
  async getAll(filters?: SecretSantaFilters): Promise<SecretSantaListResponse> {
    const response = await api.get<{ data: SecretSantaListResponse }>(`${BASE_URL}`, {
      params: filters,
    });
    return response.data.data;
  },

  /**
   * Get my Secret Santa groups
   */
  async getMyGroups(): Promise<SecretSanta[]> {
    const response = await api.get<{ data: SecretSanta[] }>(`${BASE_URL}/my`);
    return response.data.data;
  },

  /**
   * Get a single Secret Santa group by ID
   */
  async getById(id: string): Promise<SecretSanta> {
    const response = await api.get<{ data: SecretSanta }>(`${BASE_URL}/${id}`);
    return response.data.data;
  },

  /**
   * Create a new Secret Santa group
   */
  async create(data: CreateSecretSantaDto): Promise<SecretSanta> {
    const response = await api.post<{ data: SecretSanta }>(`${BASE_URL}`, data);
    return response.data.data;
  },

  /**
   * Update a Secret Santa group
   */
  async update(id: string, data: UpdateSecretSantaDto): Promise<SecretSanta> {
    const response = await api.put<{ data: SecretSanta }>(`${BASE_URL}/${id}`, data);
    return response.data.data;
  },

  /**
   * Delete a Secret Santa group
   */
  async delete(id: string): Promise<void> {
    await api.delete(`${BASE_URL}/${id}`);
  },

  /**
   * Join a Secret Santa group
   */
  async join(id: string, data?: JoinSecretSantaDto): Promise<SecretSantaParticipant> {
    const response = await api.post<{ data: SecretSantaParticipant }>(`${BASE_URL}/${id}/join`, data);
    return response.data.data;
  },

  /**
   * Leave a Secret Santa group
   */
  async leave(id: string): Promise<void> {
    await api.delete(`${BASE_URL}/${id}/leave`);
  },

  /**
   * Draw names (only creator can do this)
   */
  async draw(id: string): Promise<SecretSanta> {
    const response = await api.post<{ data: SecretSanta }>(`${BASE_URL}/${id}/draw`);
    return response.data.data;
  },

  /**
   * Get my assignment for a specific Secret Santa group
   */
  async getMyAssignment(id: string): Promise<SecretSantaAssignment> {
    const response = await api.get<{ data: SecretSantaAssignment }>(`${BASE_URL}/${id}/my-assignment`);
    return response.data.data;
  },

  /**
   * Get all my assignments across all groups
   */
  async getAllMyAssignments(): Promise<SecretSantaAssignment[]> {
    const response = await api.get<{ data: SecretSantaAssignment[] }>(`${BASE_URL}/my-assignments`);
    return response.data.data;
  },

  /**
   * Update my wishlist note for a Secret Santa group
   */
  async updateWishlist(id: string, wishlist: string): Promise<SecretSantaParticipant> {
    const response = await api.patch<{ data: SecretSantaParticipant }>(`${BASE_URL}/${id}/wishlist`, {
      wishlist,
    });
    return response.data.data;
  },
};

export default secretSantaService;
